<template>
    <div class="card investigation-card">
        <div class="table-responsive" style="min-height: 400px;">
            <table class="table custom-table-padding">
                <thead>
                    <tr>
                        <th>
                            <input
                                v-if="investigations.some(item => item.report_delivery_status === 'pending' && item.is_updated)"
                                type="checkbox"
                                class="form-check-input"
                                :checked="allSelected"
                                @change="toggleSelectAll($event)"
                            />
                        </th>
                        <th>Bill no</th>
                        <th>Report name</th>
                        <th>Service names</th>
                        <th>Sample Status</th>
                        <th class="text-center">Result</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in investigations" :key="i">
                        <td>
                            <input
                                v-if="item.report_delivery_status === 'pending' && item.is_updated"
                                type="checkbox"
                                class="form-check-input"
                                :checked="item.investigation_general_ids.split(',').every(id => investigationIds.includes(id.trim()))"
                                @input="checkIds(item.investigation_general_ids, $event)"
                            />
                        </td>
                        <td>{{ item.bill_number }}</td>
                        <td>{{ convertToTitleCase(item.report_name) }}</td>
                        <td>{{ item.service_names }}</td>
                        <td>
                            <div class="d-flex">
                                <button
                                  v-if="isClient == false"
                                  class="btn btn-dropbox btn-sm me-1"
                                  @click="goToSampleCollection(item)"
                                >
                                  <i class="fa fa-barcode"></i>
                                </button>
                            
                                <div :class="{ 'cursor-disabled': item.sample_status === 'completed' }"
                                    @click="updateSample(item)">
                                        <img
                                          :src="item.sample_status === 'pending'
                                            ? require('@/assets/check_secondary.png')
                                            : require('@/assets/check_primary.png')"
                                          alt="status"
                                          width="28"
                                          height="28"
                                        />
                                </div>
                            </div>
                        </td>
                        <td class="d-flex">
                          <div class="d-flex">
                            <button v-if="isClient == false" @click="onClickToEdit(item)" class="btn btn-primary btn-sm edit-btn">
                                <i class="fas fa-pen"></i>
                            </button>
                            <span v-if="!item.is_updated" class="text-danger">
                              Result Not Updated
                            </span>

                            <button title="With Header" v-if="item.is_updated" @click="goToOldPrint(item)" class="btn btn-secondary btn-sm edit-btn">
                              <i class="fas fa-print"></i> With Header
                            </button>

                            <button title="Without Header" v-if="item.is_updated" @click="goToOldPrint(item, true)" class="btn btn-success btn-sm edit-btn">
                              <i class="fas fa-print"></i> Without Header
                            </button>

                            <button v-if="item.status === 'approved'" class="btn btn-success btn-sm">
                                <i class="fa fa-check"></i>
                            </button>
                          </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="text-right mt-2">
              <button
                    v-if="investigationIds.length > 0"
                    type="button"
                    class="btn btn-primary"
                    @click="updateResultStatus"
                >
                    Update
              </button>
            </div>

            <p v-if="!investigations.length" class="text-center mt-5">No data</p>
        </div>
    </div>
</template>

<script setup>
    import figureFormatter from "@/services/utils/figureFormatter";
    import handleBarcode from "@/services/modules/barcode";
    import {useRoute, useRouter} from "vue-router";
    import handleHospital from "@/services/modules/hospital";
    import investigationResultPdfPrinter from '@/services/utils/investigationResultPdfPrinter';
    import investigationResultPdfPrinterOld from '@/services/utils/investigationResultPdfPrinterOld';
    import { inject, onMounted, ref, computed } from "vue";
    import handleCompany from "@/services/modules/company";
    import { useStore } from "vuex";
    import { formatDate, convertToTitleCase, toCapitalizeFirst } from '@/services/utils/global';
    import { replaceDashWithUnderscore } from '@/services/utils/global';

    const props = defineProps({
        investigations: Array,
        isClient: {
          type: Boolean,
          default: false
        },
        isPathologistDoctor: {
            type: Boolean,
            default: false
        }
    })
    const $emit = defineEmits(['onEdit', 'reloadData']);
    const {commaFormat} = figureFormatter();
    const { fetchBarcode } = handleBarcode();
    const { dateOfBarthConvertToAge, fetchSaleGeneral, updateReportStatus, updateSampleStatus } = handleHospital();
    const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
    const { exportToPDF } = investigationResultPdfPrinter();
    const { exportToPDF: oldExportToPDf } = investigationResultPdfPrinterOld();
    const $route = useRoute();
    const $router = useRouter();
    const $store = useStore();
    const showError = inject('showError');
    const showSuccess = inject('showSuccess');
    const investigationDetails = ref({});
    const companyInfo = ref({});
    const barcode = ref('');
    const qrcode = ref('');
    const barcodePatient = ref('');
    const investigationIds = ref([]);
    const customizationPdfFields = ref([]);

    const checkIds = (idsInput, event) => {
        const isChecked = event.target.checked;
        const ids = typeof idsInput === 'string' ? idsInput.split(',').map(id => id.trim()) : [String(idsInput)];

        if ( !isChecked ) {
            investigationIds.value = investigationIds.value.filter(id => !ids.includes(id));
            return
        }

        ids.forEach(id => {
            if (!investigationIds.value.includes(id)) {
                investigationIds.value.push(id);
            }
        });
    };

    const allItems = computed(() =>
        props.investigations
            .filter(item => item.report_delivery_status === 'pending' && item.is_updated)
            .flatMap(item =>
                item.investigation_general_ids.split(',').map(id => id.trim())
            )
    );

    const allSelected = computed(() =>
        allItems.value.length > 0 &&
        allItems.value.every(id => investigationIds.value.includes(id))
    );

    const toggleSelectAll = (event) => {
        const isChecked = event.target.checked;

        if( !isChecked ) {
            investigationIds.value = [];
            return;
        }

        investigationIds.value = [...new Set([...investigationIds.value, ...allItems.value])];
    };

    const updateResultStatus = async () => {
        const params = Object.assign({}, $route.params)
        
        const payload = {
            sale_master_id: parseInt(params.billId),
            investigation_ids: investigationIds.value,
            report_delivery_status: 'delivered',
            investigation_type: params.type,
            company_id: parseInt(params.companyId),
        };

        await updateReportStatus(payload).then((res) => {
            if (!res.status) {
                investigationIds.value = [];
                return showError(res.message)
            }

            showSuccess(res.message);
            $emit('reloadData');
            investigationIds.value = [];
        }).catch((e) => {
            showError('Something went wrong');
        })
    }

    const goToSampleCollection = (item) => {
      $router.push({
        name: 'sample-collection-laboratory',
        query: {
          bill_number: item.bill_number,
          barcode: item.min_id,
          party_name: item.party_name,
          investigation_ids: item.investigation_general_ids,
        },
        params: {
          billId: item.sale_master_id,
          type: item.investigation_type,
          sampleStatus: item.sample_status
        }
      })
    }

    const updateSample = async (item) => {
        if(item.sample_status === 'completed'){
            return;
        }

        if(! confirm('Are you sure you want to update sample status?')) return;

        const invesIds = item.investigation_general_ids.split(',');

        const payload = {
          sale_master_id: parseInt(item.billId),
          investigation_ids: invesIds,
          sample_status: 'completed',
          investigation_type: item.investigation_type,
          company_id: $route.params.companyId,
        };

        await updateSampleStatus(payload).then((res) => {
          if (!res.status) {
            return showError(res.message)
          }
          showSuccess(res.message);
          $emit('reloadData');
        }).catch((e) => {
            showError('Something went wrong');
        })
    }   

    const onClickToEdit = (investigationGeneral) => {
        const query = Object.assign({}, $route.query);
        query.investigation_ids = encodeURIComponent(investigationGeneral.investigation_general_ids);
        query.report_type = investigationGeneral.report_name;

        const params = Object.assign({}, $route.params)
        params.saleId = investigationGeneral.sale_master_id

        $router.push({
          name: 'pending-sample-update',
          params: params,
          query: {
            ...query,
            is_pathologist: props.isPathologistDoctor,
            status: investigationGeneral.status
          }
        })
    }
    const getQuery = (investigation) => {
        let query = `?company_id=${$route.params.companyId}`;
        if($route.params.type) query += `&investigation_type=` + replaceDashWithUnderscore($route.params.type);
        query += `&investigation_ids=${investigation.investigation_general_ids}`
        return query;
    }
    const goToPrint = async (investigation, isGeneralPdf = false) => {

        const query = getQuery(investigation);

        await fetchSaleGeneral(query, investigation.sale_master_id)
            .then((res) =>{
                if (!res.status) return showError(res.message)
                investigationDetails.value = res.data
            })
            .then( () => {
                generatePdf(companyInfo.value, investigationDetails.value, investigation, isGeneralPdf)
            }).catch( (err) => {
                console.log(err);
            })
    }

    const goToOldPrint = async (investigation, isHideHeaderFooter = false) => {

      const query = getQuery(investigation);

      await fetchSaleGeneral(query, investigation.sale_master_id)
          .then((res) =>{
            if (!res.status) return showError(res.message)
            investigationDetails.value = res.data
          })
          .then( () => {
            generateOldPdf(companyInfo.value, investigationDetails.value, investigation, isHideHeaderFooter)
          }).catch( (err) => {
            console.log(err);
          })
    }
    
    const generatePdf = async (company, data, investigation, isGeneralPdf = false) => {
        const user = $store.state.user.user;
        const isHideFooter = true;
        const isHideHeader = true;
        const isDownload = false;

        let qrcodeData = JSON.stringify({
            ref_no: data.id,
            ref_date: data.date,
            created_by: user.name,
            created_date_time: formatDate(new Date())
        })

        const barCodeQuery = `?code=${investigation.investigation_general_ids}&qrcode=${qrcodeData}&height=20`;
        const barCodePatientQuery = `?code=${data.contact_profile_id}&height=20`;

        await Promise.all([
            fetchBarcode(barCodeQuery).then((res) => {
                barcode.value = res.barcode;
                qrcode.value = res.qrcode;
            }),
            fetchBarcode(barCodePatientQuery).then((res) => {
                barcodePatient.value = res.barcode;
            })
        ]).then(() => {
            exportToPDF(
                company,
                data,
                barcode.value,
                qrcode.value,
                barcodePatient.value,
                user.name,
                convertToTitleCase(investigation.report_name),
                isHideHeader,
                isHideFooter,
                isDownload,
                customizationPdfFields.value,
                isGeneralPdf
            )
        }).catch((e) => {
            console.log('Something went wrong');
        })
    }

    const generateOldPdf = async (company, data, investigation, isHideHeaderFooter = false) => {
      const user = $store.state.user.user;
      const isHideFooter = isHideHeaderFooter;
      const isHideHeader = isHideHeaderFooter;
      const isDownload = false;

      let qrcodeData = JSON.stringify({
        ref_no: data.id,
        ref_date: data.date,
        created_by: user.name,
        created_date_time: formatDate(new Date())
      })

      const barCodeQuery = `?code=${data.bill_number}&qrcode=${qrcodeData}&height=20&width=1`;
      const barCodePatientQuery = `?code=${data.contact_profile.serial_no}&height=20`;

      await Promise.all([
        fetchBarcode(barCodeQuery).then((res) => {
          barcode.value = res.barcode;
          qrcode.value = res.qrcode;
        }),
        fetchBarcode(barCodePatientQuery).then((res) => {
          barcodePatient.value = res.barcode;
        })
      ]).then(() => {
        oldExportToPDf(
            company,
            data,
            barcode.value,
            qrcode.value,
            barcodePatient.value,
            user.name,
            convertToTitleCase(investigation.report_name),
            isHideHeader,
            isHideFooter,
            isDownload,
            customizationPdfFields.value,
        )
      }).catch((e) => {
        console.log('Something went wrong');
      })
    }
    const getCompanyInfo = async () => {
        const companyId = $route.params.companyId;

        await fetchCompanyInfo(companyId)
            .then((res) => {
                if(res.status){
                    companyInfo.value = res.data;
                }
            }).catch((error) => {
                console.log(error);
            });
    }

    const getActiveFiledCustomization = async () => {
        const companyQuery = `?company_id=${$route.params.companyId}&customization_type=pdf&type=investigation_report_pdf`;
        try {
            const res = await fetchActiveCustomizationField(companyQuery);
            customizationPdfFields.value = res.data;
        } catch (error) {
            console.error("Error fetching customization fields:", error);
        }
    }

    onMounted( async () => {
        await getCompanyInfo();
        await getActiveFiledCustomization();
    })

</script>

<style scoped>
.edit-btn{
    margin-right: 1rem;
}
.investigation-card{
    min-height: 200px
}
.image-container{
    width: 20px;
}
.avatar-image{
    width: 65px;
}
.patient-detail-th{
    width: 30%;
}
.cursor-disabled {
  cursor: not-allowed;
  pointer-events: none;
}
</style>
